// @tennis-matches/services/winterdoppel/groupTrainingCosts

import { computeTrainerSessionHourlyRate } from "../trainerstunden/trainer-session-rate.js";
import { WINTERDOPPEL_PARTICIPANT_PRICING_PER_PARTICIPANT } from "./billing.js";
import { buildWinterDoppelPlayerDisplayNames } from "./playerDisplay.js";
import { seasonAboTotal, sessionDurationHours } from "./planning.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __money(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function isWinterDoppelPerParticipantPricing(billing = {}) {
  return (
    __asStr(
      billing?.participant_pricing_mode || billing?.participantPricingMode,
    ) === WINTERDOPPEL_PARTICIPANT_PRICING_PER_PARTICIPANT
  );
}

/**
 * Effektiver EUR/Kind/Stunde: Gruppe → Serie → Trainer-Gruppensatz.
 */
export function resolveWinterDoppelParticipantHourlyRate({
  group = {},
  billing = {},
  trainer = null,
  atDate,
  groupSize = 1,
} = {}) {
  if (isWinterDoppelPerParticipantPricing(billing)) {
    const groupRate = Number(
      group?.participant_hourly_rate ?? group?.participantHourlyRate,
    );
    if (Number.isFinite(groupRate) && groupRate > 0) return __money(groupRate);
    const seriesRate = Number(
      billing?.participant_hourly_rate ?? billing?.participantHourlyRate,
    );
    if (Number.isFinite(seriesRate) && seriesRate > 0)
      return __money(seriesRate);
    return null;
  }
  return resolveWinterDoppelGroupTrainerHourlyRate(trainer, {
    atDate,
    groupSize: Math.max(1, groupSize),
  });
}

function groupMemberIds(group = {}) {
  return [
    ...new Set(
      (Array.isArray(group.playerIds)
        ? group.playerIds
        : (group.members || []).map((m) => m.player_id)
      )
        .map((id) => __asStr(id))
        .filter(Boolean),
    ),
  ];
}

function lookupTrainer(trainersById, trainerId) {
  const id = __asStr(trainerId);
  if (!id || !trainersById) return null;
  if (trainersById instanceof Map) return trainersById.get(id) || null;
  return trainersById[id] || null;
}

function lookupDates(datesByGroupId, groupId, group) {
  const id = __asStr(groupId || group?.id);
  if (datesByGroupId instanceof Map && id) {
    const fromMap = datesByGroupId.get(id);
    if (Array.isArray(fromMap)) return fromMap;
  } else if (datesByGroupId && id && Array.isArray(datesByGroupId[id])) {
    return datesByGroupId[id];
  }
  if (Array.isArray(group?.dates)) return group.dates;
  return [];
}

/**
 * Termine einer Gruppe aus der Grobplanung: Sessions, in denen
 * mind. 2 Gruppenmitglieder als Starter stehen (wie „Geplante Kosten“).
 * @returns {string[]}
 */
export function buildWinterDoppelGroupSessionDates({
  group = {},
  sessions = [],
  sessionPlayers = [],
} = {}) {
  const memberIds = new Set(groupMemberIds(group));
  if (memberIds.size < 2) return [];

  const active = (sessions || []).filter(
    (s) => s?.id && s.status !== "cancelled" && s.session_date,
  );
  /** @type {Map<string, object[]>} */
  const bySession = new Map();
  for (const sp of sessionPlayers || []) {
    const sid = String(sp?.session_id || "");
    if (!sid) continue;
    const arr = bySession.get(sid) || [];
    arr.push(sp);
    bySession.set(sid, arr);
  }

  const dates = [];
  for (const s of active) {
    const sps = bySession.get(String(s.id)) || [];
    let startersInGroup = 0;
    for (const sp of sps) {
      if (sp.role !== "starter" || sp.rsvp === "no") continue;
      if (!memberIds.has(String(sp.player_id || ""))) continue;
      startersInGroup += 1;
    }
    if (startersInGroup >= 2) {
      dates.push(String(s.session_date).slice(0, 10));
    }
  }
  return dates;
}

/**
 * Teilnehmer-Stundensatz (was Eltern zahlen) – nicht Honorar/Auszahlung.
 * @returns {number|null}
 */
export function resolveWinterDoppelGroupTrainerHourlyRate(trainer, opts = {}) {
  if (!trainer) return null;
  return computeTrainerSessionHourlyRate(trainer, {
    atDate: opts.atDate,
    targetGroup: "youth",
    trainingType: "group",
    groupSize: opts.groupSize,
  });
}

/**
 * Trainingskosten einer Gruppe.
 * hourly_split: rate × Stunden × Termine (Gruppe), dann / Mitglieder.
 * per_participant_hourly: rate × Stunden × Termine je Kind (unabhängig von Gruppengröße).
 */
export function estimateWinterDoppelGroupTrainingCost({
  group = {},
  trainer = null,
  billing = {},
  dates = [],
} = {}) {
  const memberIds = groupMemberIds(group);
  const dateList = (Array.isArray(dates) ? dates : [])
    .map((d) => String(d || "").slice(0, 10))
    .filter(Boolean);
  const sessionCount = dateList.length;
  const hours = sessionDurationHours(
    group.start_time || group.startTime,
    group.end_time || group.endTime,
  );
  const atDate =
    dateList[0] ||
    __asStr(group.schedule_start || group.scheduleStart).slice(0, 10) ||
    undefined;
  const perParticipant = isWinterDoppelPerParticipantPricing(billing);
  const rate = resolveWinterDoppelParticipantHourlyRate({
    group,
    billing,
    trainer,
    atDate,
    groupSize: Math.max(1, memberIds.length),
  });
  const perMember =
    rate != null && hours > 0 && sessionCount > 0
      ? __money(rate * hours * sessionCount)
      : 0;
  const trainingTotal = perParticipant
    ? __money(perMember * memberIds.length)
    : rate != null && hours > 0 && sessionCount > 0
      ? __money(rate * hours * sessionCount)
      : 0;
  const sharePerMember = perParticipant
    ? perMember
    : memberIds.length > 0
      ? __money(trainingTotal / memberIds.length)
      : 0;
  return {
    trainerId: __asStr(group.trainer_id || group.trainerId) || null,
    rate,
    hours,
    sessionCount,
    trainingTotal,
    perMember: sharePerMember,
    memberIds,
    pricingMode: perParticipant
      ? WINTERDOPPEL_PARTICIPANT_PRICING_PER_PARTICIPANT
      : "hourly_split",
  };
}

/**
 * Hallen-Abo (Serie) + Trainerkosten je Gruppe, Anteile je Spieler.
 */
export function buildWinterDoppelGroupCostBreakdown({
  groups = [],
  trainersById = {},
  players = [],
  series = {},
  billing = {},
  datesByGroupId = {},
} = {}) {
  const courtCount = Math.max(1, Number(series?.court_count) || 1);
  const hourly = Number(billing?.hourly_rate ?? series?.hourly_rate) || 0;
  const hallTotal = seasonAboTotal(hourly, courtCount, {
    startTime: series?.start_time,
    endTime: series?.end_time,
  });
  const playerList = (players || []).filter((p) => p?.id);
  const playerCount = Math.max(1, playerList.length);
  const hallSharePerPlayer = Math.round((hallTotal / playerCount) * 100) / 100;

  const nameById = buildWinterDoppelPlayerDisplayNames(playerList);
  /** @type {Map<string, number>} */
  const trainerShareByPlayer = new Map();
  const groupRows = [];
  let trainerTotal = 0;

  for (const group of groups || []) {
    const trainerId = __asStr(group?.trainer_id || group?.trainerId);
    const trainer = lookupTrainer(trainersById, trainerId);
    const dates = lookupDates(datesByGroupId, group?.id, group);
    const est = estimateWinterDoppelGroupTrainingCost({
      group,
      trainer,
      billing,
      dates,
    });
    trainerTotal = Math.round((trainerTotal + est.trainingTotal) * 100) / 100;
    for (const pid of est.memberIds) {
      const prev = trainerShareByPlayer.get(pid) || 0;
      trainerShareByPlayer.set(
        pid,
        Math.round((prev + est.perMember) * 100) / 100,
      );
    }
    const trainerName = trainer
      ? `${__asStr(trainer.first_name)} ${__asStr(trainer.last_name)}`.trim()
      : "";
    groupRows.push({
      groupId: __asStr(group.id) || null,
      groupName: __asStr(group.name) || "",
      trainerId: est.trainerId,
      trainerName,
      rate: est.rate,
      hours: est.hours,
      sessionCount: est.sessionCount,
      trainingTotal: est.trainingTotal,
      perMember: est.perMember,
      memberCount: est.memberIds.length,
    });
  }

  const playerRows = playerList.map((p) => {
    const playerId = String(p.id);
    const trainerShare = trainerShareByPlayer.get(playerId) || 0;
    return {
      playerId,
      displayName: nameById.get(playerId) || p.display_name || playerId,
      hallShare: hallSharePerPlayer,
      trainerShare,
      total: Math.round((hallSharePerPlayer + trainerShare) * 100) / 100,
    };
  });

  return {
    hallTotal,
    trainerTotal,
    grandTotal: Math.round((hallTotal + trainerTotal) * 100) / 100,
    hallSharePerPlayer,
    groupRows,
    playerRows,
  };
}
